"use client"

import * as React from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { cn } from "@/lib/utils"
import {
  Activity,
  Calendar,
  Home,
  Users,
  Workflow,
  FolderClosed,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Bell,
  Target,
  UserCheck,
  ClipboardList,
  Link2
} from "lucide-react"

import { ScrollArea } from "@/components/ui/scroll-area"
import { BrandLogo } from "@/components/BrandLogo"
import { useAuth } from "@/hooks/use-auth"
import { Button } from "@/components/ui/button"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "@/hooks/use-toast"
import { useSidebar } from "@/components/ui/sidebar"
import { useFeatureAccess, FeatureName } from "@/hooks/use-feature-access"
import { NotificationsPopover } from "@/components/notifications/NotificationsPopover"

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {}

export function Sidebar({ className, ...props }: SidebarProps) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = React.useState(false);
  const { state, setOpen } = useSidebar();
  const { hasAccess, userPlan } = useFeatureAccess();
  
  const routes = [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: Home,
      feature: "dashboard" as FeatureName,
    },
    {
      name: "Clientes",
      href: "/clients",
      icon: Users,
      feature: "clients" as FeatureName,
    },
    {
      name: "Fluxo de Trabalho",
      href: "/workflow",
      icon: Workflow,
      feature: "workflow" as FeatureName,
    },
    {
      name: "Processos",
      href: "/processes",
      icon: ClipboardList,
      feature: "dashboard" as FeatureName,
    },
    {
      name: "Aprovações",
      href: "/approvals",
      icon: Activity,
      feature: "approvals" as FeatureName,
    },
    {
      name: "Metas",
      href: "/goals",
      icon: Target,
      feature: "dashboard" as FeatureName,
    },
    {
      name: "Calendário",
      href: "/calendar",
      icon: Calendar,
      feature: "calendar" as FeatureName,
    },
    {
      name: "Finanças",
      href: "/finances",
      icon: FolderClosed,
      feature: "finances" as FeatureName,
    },
    {
      name: "Link Curto",
      href: "/link-short",
      icon: Link2,
      feature: "dashboard" as FeatureName,
    },
    {
      name: "Colaboradores",
      href: "/collaborators",
      icon: UserCheck,
      feature: "dashboard" as FeatureName,
    },
    {
      name: "Configurações",
      href: "/settings",
      icon: Settings,
      feature: "dashboard" as FeatureName,
    },
  ];
  
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success("Logout realizado com sucesso");
      navigate("/login");
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
      toast.error("Erro ao fazer logout");
    }
  };

  React.useEffect(() => {
    // Syncronize collapsed state from sidebar provider
    setCollapsed(state === 'collapsed');
  }, [state]);

  const toggleCollapsed = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    setOpen(!newState);
  };

  // Logo section at the top of the sidebar
  const renderLogo = () => (
    <div className={cn(
      "flex items-center px-6 py-4 transition-opacity duration-200",
      collapsed ? "opacity-0 h-0 py-0 overflow-hidden" : "opacity-100"
    )}>
      <BrandLogo agencyName={user?.agency_name} size="md" />
      {!collapsed && (
        <div className="ml-auto">
          <NotificationsPopover />
        </div>
      )}
    </div>
  );

  return (
    <div 
      className={cn(
        "relative pb-12 h-full bg-background border-r border-border transition-all duration-200", 
        collapsed ? "w-[70px]" : "w-[240px]",
        className
      )} 
      {...props}
    >
      {renderLogo()}
      {collapsed && (
        <div className="flex justify-center py-2">
          <NotificationsPopover />
        </div>
      )}
      <Button 
        variant="ghost" 
        size="icon"
        className="fixed top-6 h-8 w-8 rounded-full border bg-background shadow-lg hover:shadow-xl transition-all duration-200"
        style={{
          left: collapsed ? "54px" : "224px",
          zIndex: 9999
        }}
        onClick={toggleCollapsed}
      >
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </Button>
      
      <ScrollArea className="h-[calc(100vh-16rem)] pb-4">
        <div className="px-3 py-2">
          <nav className="grid items-start gap-2">
            {routes.map((route, index) => {
              const isActive = location.pathname === route.href || 
                (route.href !== "/" && location.pathname.startsWith(route.href));
              const featureEnabled = hasAccess(route.feature);
              
              return (
                <Link
                  key={index}
                  to={route.href}
                  className={cn(
                    "group flex items-center rounded-md px-3 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-all",
                    isActive ? "bg-primary text-primary-foreground" : "transparent",
                  )}
                  title={collapsed ? route.name : undefined}
                >
                  <route.icon className={cn("h-4 w-4", collapsed ? "mx-auto" : "mr-3")} />
                  {!collapsed && <span>{route.name}</span>}
                </Link>
              );
            })}
          </nav>
        </div>
      </ScrollArea>
      
      {/* User info and logout section */}
      <div className="absolute bottom-0 left-0 w-full p-4">
        <div className="flex flex-col space-y-2">
          {user && !collapsed && (
            <div className="text-sm text-muted-foreground px-2">
              <span className="block text-xs">Logado como:</span>
              <span className="font-medium block truncate max-w-[180px]">{user.email}</span>
            </div>
          )}
          <Button 
            variant="ghost" 
            size="sm"
            className={cn(
              "w-full justify-start text-xs py-1 h-8",
              collapsed && "justify-center px-0"
            )}
            onClick={handleLogout}
            title={collapsed ? "Sair da conta" : undefined}
          >
            <LogOut className={cn("h-3 w-3", collapsed ? "mr-0" : "mr-2")} />
            {!collapsed && "Sair da conta"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Export just the fixed sidebar component
export { Sidebar as SidebarNav };
